import { Info } from 'lucide-react';
import TooltipWrapper, {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button, Table } from '@/components/ui/';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useExchangeInfo } from '@/features/markets/api/get-exchange-info';
import { useFetchAccount } from '../api/get-account';
import {
  ErrorCode,
  ErrorMsgs,
  NetworkType,
  OrderStatus,
  OrderType,
  TransferType,
} from '@/types/enums';
import {
  useTransfersStore,
  VerifiedTransfer,
} from '@/store/use-transfers-store';
import {
  bigIntToNum,
  formatBigInt,
  formatDate,
  formatOrderStatus,
  formatTransferType,
  parseDecimalToBigInt,
} from '@/utils/value-format';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';
import { chains, SOLANA_CHAIN_ID } from '@/lib/wagmi/config';
import { Chain } from 'wagmi/chains';
import { useUnifiedAccount } from '@/hooks';
import { useAccountStore } from '@/store/use-account-store';
import {
  WITHDRAW_THRESHOLD_AMOUNT,
  WITHDRAW_THRESHOLD_HOURS,
} from '@/components/header/modals/transfer-dialog';
import { useTransfers } from '../api/get-transfers';
import { ReloadIcon } from '@radix-ui/react-icons';
import { useModalStore } from '@/store/use-modal-store';
import { useAccount } from 'wagmi';
import { Spinner } from '@/components/ui/spinner';

type TransferColumn = {
  id: string;
  postTime: number;
  transferType: TransferType;
  orderType: TransferType;
  size?: bigint;
  status: OrderStatus;
  code?: ErrorCode;
  chain: Chain;
  chainId: number;
  requestArgs?: string;
  signer?: string;
  signature?: string;
  signatureProof?: string;
  validatorSignature?: string;
  isSolanaNative?: boolean;
};

const getDifferenceInHours = (date1: number, date2: number) =>
  Math.abs(date1 - date2) / (1000 * 60 * 60);

export default function TransfersTable() {
  const { chain, address, networkType } = useUnifiedAccount();
  const { isConnected } = useAccount();

  const { transfers: allTransfers } = useTransfersStore();

  const transfers = useMemo(
    () => (address ? allTransfers[address] ?? [] : []),
    [address, allTransfers],
  );

  const getAccurateTime = useAccountStore((state) => state.getAccurateTime);

  const { isLoading } = useTransfers();
  const {
    handleWithdrawConfirmationModalOpen,
    setVerifiedTransfer,
    handledVerifiedTransferID,
  } = useModalStore();

  const data: TransferColumn[] = useMemo(() => {
    return Object.values(transfers)
      .filter(
        (transfer) =>
          chains.some((chain) => chain.id === transfer.chainId) ||
          SOLANA_CHAIN_ID === transfer.chainId,
      )
      .map(
        (transfer) =>
          ({
            ...transfer,
            id: transfer.id,
            postTime: transfer.postTime,
            transferType: transfer.orderType,
            size:
              transfer.status === OrderStatus.REJECTED
                ? undefined
                : transfer.size,
            status: transfer.status,
            code:
              transfer.status === OrderStatus.REJECTED
                ? transfer.code
                : undefined,
            chain:
              networkType === NetworkType.SOLANA
                ? { name: 'SOLANA MAINNET BETA' }
                : chains.find((chain) => chain.id === transfer.chainId),
          }) as TransferColumn,
      )
      .sort((a, b) => Number(b.postTime - a.postTime));
  }, [transfers, address]);

  const handleWithdraw = (transfer: TransferColumn) => {
    setVerifiedTransfer(transfer as unknown as VerifiedTransfer);
    handleWithdrawConfirmationModalOpen(true);
  };

  const WithdrawButton = ({ transfer }: { transfer: TransferColumn }) => {
    const isProcessing = transfer.id == handledVerifiedTransferID;

    return (
      <TooltipWrapper
        direction="left"
        content={
          <>
            {!isProcessing ? (
              <>
                Withdrawal order is validated. <br />
                {!isConnected ? 'Connect EVM wallet and c' : 'C'}lick the button
                to complete the transaction from your wallet.
              </>
            ) : (
              <>
                Withdrawal transaction is being processed. <br /> Please wait
                for a few minutes.
              </>
            )}
          </>
        }
        className="hidden md:block"
      >
        <Button
          variant="secondary"
          disabled={!isConnected || isProcessing}
          onClick={() => handleWithdraw(transfer)}
        >
          {isProcessing ? <Spinner className="h-6 w-6" /> : 'Verified'}
        </Button>
      </TooltipWrapper>
    );
  };

  const columns = useMemo<ColumnDef<TransferColumn>[]>(
    () => [
      {
        accessorFn: ({ postTime }) =>
          formatDate(new Date(postTime), {
            showTime: true,
            showSeconds: true,
          }),
        header: 'TIME',
        meta: { width: 'md:w-[12.5%]' },
        sortingFn: (a, b) => a.original.postTime - b.original.postTime,
      },
      {
        accessorFn: ({ chain }) => chain.name,
        header: 'CHAIN',
        meta: { width: 'md:w-[10%] hidden md:table-cell' },
      },
      {
        accessorFn: ({ transferType }) =>
          transferType ? formatTransferType(transferType) : '-',
        header: 'TYPE',
        meta: { width: 'md:w-[10%]' },
      },
      {
        accessorFn: ({ size }) =>
          formatBigInt(size, COLLATERAL_DECIMALS, {
            digits: 2,
            round: 'ceil',
          }),
        header: 'AMOUNT',
        meta: { width: 'md:w-[10%]' },
        sortingFn: (a, b) =>
          bigIntToNum(a.original.size || 0n, COLLATERAL_DECIMALS) -
          bigIntToNum(b.original.size || 0n, COLLATERAL_DECIMALS),
      },
      {
        accessorKey: 'status',
        cell: ({ row }) => {
          const { status, code, transferType, size, postTime, chain } =
            row.original;

          const cumulatedAmount = data
            .filter(
              (transfer) =>
                transfer.transferType === TransferType.WITHDRAW &&
                transfer.status === OrderStatus.NEW &&
                transfer.postTime <= postTime &&
                getDifferenceInHours(postTime, transfer.postTime) <= 24,
            )
            .reduce((prev, transfer) => prev + (transfer.size || 0n), 0n);
          return (
            <>
              <div className="hidden items-center justify-end gap-4 leading-none md:flex md:justify-normal">
                {status
                  ? status != OrderStatus.VERIFIED
                    ? formatOrderStatus(status)
                    : ''
                  : '-'}
                {status === OrderStatus.VERIFIED && (
                  <WithdrawButton transfer={row.original} />
                )}
                {status === OrderStatus.REJECTED && code && (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger>
                        <Info size={14} />
                      </TooltipTrigger>
                      <TooltipContent>{ErrorMsgs[code]}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {status === OrderStatus.NEW &&
                  transferType === TransferType.WITHDRAW &&
                  size !== undefined && (
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger>
                          <Info size={14} />
                        </TooltipTrigger>
                        <TooltipContent>
                          {cumulatedAmount >=
                          parseDecimalToBigInt(
                            WITHDRAW_THRESHOLD_AMOUNT.toString(),
                            COLLATERAL_DECIMALS,
                          )
                            ? `Please wait ${Math.ceil(WITHDRAW_THRESHOLD_HOURS - getDifferenceInHours(getAccurateTime(), postTime))} more hours`
                            : SOLANA_CHAIN_ID === chain.id
                              ? 'Withdrawal processed by the platform and sent to CCTP Circle for execution, usually takes about 15 minutes but may take longer'
                              : 'To be fulfilled soon.'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
              </div>
              <span className="md:hidden">
                {status
                  ? status != OrderStatus.VERIFIED
                    ? formatOrderStatus(status)
                    : ''
                  : '-'}
                {status === OrderStatus.VERIFIED && (
                  <WithdrawButton transfer={row.original} />
                )}
              </span>
            </>
          );
        },
        header: () => (
          <div className="flex w-full justify-center md:justify-start">
            STATUS
          </div>
        ),
        meta: { width: 'md:w-[10%] text-right md:text-left' },
      },
    ],
    [data, isConnected, handledVerifiedTransferID],
  );

  return (
    <Table
      data={data}
      // @ts-ignore
      columns={columns}
      loading={isLoading}
      // className="[&_thead]:hidden md:[&_thead]:table-header-group"
      className="[&_th]:whitespace-nowrap"
    />
  );
}
